import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { AlertCircleIcon, AlertTriangleIcon, InfoIcon, CheckCircleIcon, XIcon, CheckIcon } from 'lucide-react';
interface Notification {
  id: number;
  type: 'alert' | 'warning' | 'info' | 'success';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  link: string;
}
interface NotificationsDropdownProps {
  notifications: Notification[];
  onMarkAsRead: (id: number) => void;
  onMarkAllAsRead: () => void;
  onDelete: (id: number) => void;
  onClose: () => void;
}
const NotificationsDropdown: React.FC<NotificationsDropdownProps> = ({
  notifications,
  onMarkAsRead,
  onMarkAllAsRead,
  onDelete,
  onClose
}) => {
  const navigate = useNavigate();
  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'alert':
        return <AlertCircleIcon size={20} className="text-red-500" data-id="element-123" />;
      case 'warning':
        return <AlertTriangleIcon size={20} className="text-yellow-500" data-id="element-124" />;
      case 'success':
        return <CheckCircleIcon size={20} className="text-green-500" data-id="element-125" />;
      case 'info':
      default:
        return <InfoIcon size={20} className="text-blue-500" data-id="element-126" />;
    }
  };
  const getNotificationBg = (type: string, read: boolean) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'alert':
        return 'bg-red-50';
      case 'warning':
        return 'bg-yellow-50';
      case 'success':
        return 'bg-green-50';
      case 'info':
      default:
        return 'bg-blue-50';
    }
  };
  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      onMarkAsRead(notification.id);
    }
    navigate(notification.link);
    onClose();
  };
  const unreadCount = notifications.filter(n => !n.read).length;
  return <motion.div initial={{
    opacity: 0,
    y: -10
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.2
  }} className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50 overflow-hidden" data-id="element-127">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 flex justify-between items-center bg-gray-50" data-id="element-128">
        <div data-id="element-129">
          <h3 className="text-sm font-semibold text-gray-800" data-id="element-130">
            Notificaciones
          </h3>
          {unreadCount > 0 && <p className="text-xs text-gray-500" data-id="element-131">{unreadCount} sin leer</p>}
        </div>
        {unreadCount > 0 && <button onClick={onMarkAllAsRead} className="text-xs text-blue-600 hover:text-blue-700 font-medium" data-id="element-132">
            Marcar todas como leídas
          </button>}
      </div>
      {/* Notifications List */}
      <div className="max-h-96 overflow-y-auto" data-id="element-133">
        {notifications.length === 0 ? <div className="px-4 py-8 text-center" data-id="element-134">
            <InfoIcon size={32} className="mx-auto text-gray-300 mb-2" data-id="element-135" />
            <p className="text-sm text-gray-500" data-id="element-136">No tienes notificaciones</p>
          </div> : <AnimatePresence data-id="element-137">
            {notifications.map((notification, index) => <motion.div key={notification.id} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} transition={{
          duration: 0.2,
          delay: index * 0.05
        }} className={`relative border-b border-gray-100 last:border-b-0 ${getNotificationBg(notification.type, notification.read)}`} data-id="element-138">
                <div className="px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => handleNotificationClick(notification)} data-id="element-139">
                  <div className="flex items-start space-x-3" data-id="element-140">
                    <div className="flex-shrink-0 mt-0.5" data-id="element-141">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-142">
                      <div className="flex items-start justify-between" data-id="element-143">
                        <p className={`text-sm font-medium ${notification.read ? 'text-gray-600' : 'text-gray-900'}`} data-id="element-144">
                          {notification.title}
                        </p>
                        {!notification.read && <div className="w-2 h-2 bg-blue-600 rounded-full ml-2 flex-shrink-0 mt-1.5" data-id="element-145"></div>}
                      </div>
                      <p className={`text-xs mt-1 ${notification.read ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-146">
                        {notification.message}
                      </p>
                      <p className="text-xs text-gray-400 mt-1" data-id="element-147">
                        {notification.timestamp}
                      </p>
                    </div>
                  </div>
                </div>
                {/* Action Buttons */}
                <div className="absolute top-2 right-2 flex space-x-1" data-id="element-148">
                  {!notification.read && <button onClick={e => {
              e.stopPropagation();
              onMarkAsRead(notification.id);
            }} className="p-1 rounded hover:bg-white transition-colors" title="Marcar como leída" data-id="element-149">
                      <CheckIcon size={14} className="text-gray-400 hover:text-green-600" data-id="element-150" />
                    </button>}
                  <button onClick={e => {
              e.stopPropagation();
              onDelete(notification.id);
            }} className="p-1 rounded hover:bg-white transition-colors" title="Eliminar" data-id="element-151">
                    <XIcon size={14} className="text-gray-400 hover:text-red-600" data-id="element-152" />
                  </button>
                </div>
              </motion.div>)}
          </AnimatePresence>}
      </div>
      {/* Footer */}
      {notifications.length > 0 && <div className="px-4 py-3 border-t border-gray-200 bg-gray-50" data-id="element-153">
          <button onClick={onClose} className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium" data-id="element-154">
            Ver todas las notificaciones
          </button>
        </div>}
    </motion.div>;
};
export default NotificationsDropdown;